import { useState, useEffect } from "react";
import { Button, Icon, Loading, Modal, Typography } from "@/components/atoms";
import { getErrorMessages } from "@/helpers/reduxHelpers";
import { useUpdateNetworkTierMutation } from "@/redux/apis/network/networkApi";
import { NetworkItem } from "@/redux/slices/network/types";
import { addToast, clsx, getAssetUrl } from "@/utils";

interface EditOperatorTierModalProps {
  open: boolean;
  onClose: () => void;
  network: NetworkItem | undefined;
}

const TIER_OPTIONS = [
  { tier: 1, label: "Tier 1", subtitle: "Primary", description: "Dispatched first" },
  { tier: 2, label: "Tier 2", subtitle: "Secondary", description: "If Tier 1 is unavailable" },
  { tier: 3, label: "Tier 3", subtitle: "Tertiary", description: "Last fallback" },
];

export const EditOperatorTierModal = ({ open, onClose, network }: EditOperatorTierModalProps) => {
  const [selectedTier, setSelectedTier] = useState<number>(3);
  const [updateTier, { isLoading }] = useUpdateNetworkTierMutation();

  useEffect(() => {
    if (open && network) {
      setSelectedTier(network.tier ?? 3);
    }
  }, [open, network]);

  const handleSave = () => {
    if (!network) return;

    updateTier({ id: network.id, tier: selectedTier })
      .unwrap()
      .then(() => {
        addToast("success", "Operator tier updated successfully");
        onClose();
      })
      .catch((e) => getErrorMessages(e).forEach((m) => addToast("danger", m)));
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      variant="web-small"
      title="Edit Operator Tier"
      renderButtons={() => (
        <>
          <Button variant="secondary" size="lg" onClick={onClose} className="flex-1">
            Cancel
          </Button>
          <Button variant="primary" size="lg" onClick={handleSave} className="flex-1">
            Save Changes
          </Button>
        </>
      )}
    >
      {isLoading && <Loading />}
      <div className="flex flex-col gap-5">
        {/* Operator info card */}
        <div className="flex items-center gap-3 rounded-lg border border-neutral-mid-gray p-4">
          {network?.operator.assetsCdn.emblem ? (
            <img src={getAssetUrl(network.operator.assetsCdn.emblem)} className="h-10 w-10 rounded-lg" />
          ) : (
            <div className="h-10 w-10 rounded-lg bg-neutral-mid-gray" />
          )}
          <div className="flex flex-col">
            <Typography variant="action">{network?.operator.name}</Typography>
            {network?.operator.providerCode && (
              <div className="flex items-center gap-1">
                <Icon name="Global" size="sm" className="text-neutral-dark-gray" />
                <Typography variant="small" className="text-neutral-dark-gray">
                  Operator Code: {network.operator.providerCode}
                </Typography>
              </div>
            )}
          </div>
        </div>

        {/* Tier selector */}
        <div className="flex flex-col gap-3">
          <Typography variant="action">Select Operator Tier</Typography>
          <div className="flex gap-3">
            {TIER_OPTIONS.map((option) => {
              const isSelected = selectedTier === option.tier;
              return (
                <button
                  key={option.tier}
                  type="button"
                  onClick={() => setSelectedTier(option.tier)}
                  className={clsx(
                    "relative flex flex-1 flex-col gap-2 rounded-lg border p-4 text-left transition-colors",
                    isSelected ? "border-primary bg-primary-light" : "border-neutral-mid-gray"
                  )}
                >
                  <div className="flex w-full items-start justify-between">
                    <span
                      className={clsx(
                        "rounded-md border px-2 py-0.5 text-sm font-semibold",
                        isSelected ? "border-primary text-primary" : "border-neutral-mid-gray text-neutral-black"
                      )}
                    >
                      {option.label}
                    </span>
                    {isSelected && <Icon name="TickCircle" variant="Bold" size="md" className="text-success" />}
                  </div>
                  <div className="flex flex-col">
                    <Typography variant="action" className={clsx(isSelected && "text-primary")}>
                      {option.subtitle}
                    </Typography>
                    <Typography variant="small" className="text-neutral-dark-gray">
                      {option.description}
                    </Typography>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <Typography variant="paragraph" className="text-neutral-dark-gray">
          Sets dispatch priority — Tier 1 operators receive jobs first, followed by Tier 2 and Tier 3. Can be changed
          anytime from your Private Network.
        </Typography>
      </div>
    </Modal>
  );
};
